import React from "react";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericMultiDropdown from "@ui-components/Dropdown/GenericMultiDropdown";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { getServiceDropdownLabel } from "../../utils/tickets";
import type { CustomerInfo, ServiceItem } from "../../utils/tickets";
import { FieldRow, FormField } from "../requestForm/FormField";

interface KontrolEdgeServiceSelectionProps {
	/** Partner view also has to say which end customer the ticket is for. */
	isPartnerView: boolean;
	uniqueCustomers: CustomerInfo[];
	/** Already narrowed to the selected customer by the calling modal. */
	filteredServiceItems: ServiceItem[];
	selectedCustomerId: number | null;
	selectedCustomerNameDisplay: string;
	selectedServiceLabels: string[];
	onCustomerSelect: (customerNameDisplay: string) => void;
	onServiceSelect: (selectedLabels: string[]) => void;
	servicesHint?: string;
}

/**
 * Customer + service(s) pickers shared by the Kontrol Edge ticket modals.
 *
 * The pair travels together: the services list is derived from the customer,
 * so splitting them across surfaces is what let Help Request ship without any
 * service at all (CTX-533). Both feed `subscriptionID`, which the backend
 * sends to KS as the Service ID custom field.
 */
export const KontrolEdgeServiceSelection: React.FC<
	KontrolEdgeServiceSelectionProps
> = ({
	isPartnerView,
	uniqueCustomers,
	filteredServiceItems,
	selectedCustomerId,
	selectedCustomerNameDisplay,
	selectedServiceLabels,
	onCustomerSelect,
	onServiceSelect,
	servicesHint = "You can select more than one.",
}) => {
	// On a phone the lists open under their field (see ParticipantPicker).
	const viewportMenus = useWindowSize().width >= 640;
	// Without a customer there is nothing to list, so the services dropdown
	// stays closed until one is picked rather than showing an empty menu.
	const servicesDisabled = isPartnerView && !selectedCustomerId;

	const services = (
		<FormField
			label="Service(s)"
			required
			hint={servicesDisabled ? "Pick the customer first." : servicesHint}
		>
			<GenericMultiDropdown
				items={filteredServiceItems.map(getServiceDropdownLabel)}
				value={selectedServiceLabels}
				onSelect={onServiceSelect}
				defaultLabel="Select service(s)"
				minWidthClass=""
				maxWidthClass=""
				widthClass="w-full"
				disabled={servicesDisabled}
				fixedPosition={viewportMenus}
				dropdownMaxHeightClass="max-h-48"
			/>
		</FormField>
	);

	if (!isPartnerView) return services;

	return (
		<FieldRow>
			<FormField label="Customer" required>
				<GenericSearchableDropdown
					items={uniqueCustomers.map((c) => c.nameDisplay)}
					value={selectedCustomerNameDisplay}
					onSelect={onCustomerSelect}
					defaultLabel="Select a customer"
					allowCustomValues={false}
					minWidthClass=""
					maxWidthClass=""
					widthClass="w-full"
					dropdownMaxHeightClass="max-h-48"
					fixedPosition={viewportMenus}
				/>
			</FormField>
			{services}
		</FieldRow>
	);
};

export default KontrolEdgeServiceSelection;
